import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Marco } from './componentes/Marco';
import { Cargando, Vacio } from './componentes/Piezas';
import { tieneAlguno } from './comun/permisos';
import { PaginaIngreso } from './paginas/PaginaIngreso';
import { useSesion } from './sesion/sesion';

const PaginaMostrador = lazy(() => import('./paginas/PaginaMostrador'));
const PaginaTablero = lazy(() => import('./paginas/PaginaTablero'));
const PaginaIntegrantes = lazy(() => import('./paginas/PaginaIntegrantes'));
const PaginaActividades = lazy(() => import('./paginas/PaginaActividades'));
const PaginaListado = lazy(() => import('./paginas/PaginaListado'));
const PaginaPagos = lazy(() => import('./paginas/PaginaPagos'));
const PaginaComprobante = lazy(() => import('./paginas/PaginaComprobante'));
const PaginaCatalogos = lazy(() => import('./paginas/PaginaCatalogos'));
const PaginaEquipo = lazy(() => import('./paginas/PaginaEquipo'));
const PaginaAuditoria = lazy(() => import('./paginas/PaginaAuditoria'));
const PaginaMiCuenta = lazy(() => import('./paginas/PaginaMiCuenta'));
const PaginaPlataforma = lazy(() => import('./paginas/PaginaPlataforma'));
const PaginaNoIncluido = lazy(() => import('./paginas/PaginaNoIncluido'));
const PaginaElegirEntidad = lazy(() => import('./paginas/PaginaElegirEntidad'));

/**
 * Ocultar una pantalla es cortesía; la API verifica el permiso igual. Lo que NO se hace es
 * mostrar un enlace que lleva a un 403: el menú se deriva de los permisos que devolvió
 * `/auth/yo`, y estas guardas son la misma regla aplicada a la ruta escrita a mano.
 */
function Requiere({ permisos, children }: { permisos: string[]; children: ReactNode }) {
  const { estado } = useSesion();
  if (estado.tipo !== 'adentro') return null;
  if (!tieneAlguno(estado.yo.permisos, permisos)) {
    return (
      <Vacio
        icono="candado"
        titulo="Tu rol no tiene acceso a esta sección"
        texto="Si lo necesitás, pedíselo a quien administra los roles de tu entidad."
      />
    );
  }
  return <>{children}</>;
}

const cargando = (
  <div className="contenido">
    <Cargando filas={6} />
  </div>
);

export function App() {
  const { estado } = useSesion();

  if (estado.tipo === 'cargando') return cargando;
  if (estado.tipo === 'afuera') return <PaginaIngreso />;

  const { yo } = estado;

  // Sesión sin entidad: o elige entre las suyas, o es soporte de RASP y va a la plataforma.
  if (!yo.entidad && !yo.usuario.esPlataforma) {
    return (
      <Suspense fallback={cargando}>
        {yo.entidades.length > 0 ? (
          <PaginaElegirEntidad />
        ) : (
          <div className="contenido">
            <Vacio
              icono="candado"
              titulo="Tu usuario no está habilitado en ninguna entidad"
              texto="Pedile a quien administra tu entidad que te dé acceso, o a RASP si sos de soporte."
            />
          </div>
        )}
      </Suspense>
    );
  }

  // La pantalla de inicio depende de lo que la persona pueda hacer: al cajero le sirve el
  // mostrador, no el tablero.
  const inicio = tieneAlguno(yo.permisos, ['tablero.ver']) ? '/' : '/mostrador';

  return (
    <Suspense fallback={cargando}>
      <Routes>
        {/* El comprobante se imprime sin el marco de la aplicación. */}
        <Route
          path="/pagos/:id/comprobante"
          element={
            <Requiere permisos={['pagos.ver', 'mostrador.pagar']}>
              <PaginaComprobante />
            </Requiere>
          }
        />
        <Route
          path="*"
          element={
            <Marco>
              <Suspense fallback={<Cargando filas={6} />}>
                {yo.entidad ? (
                  <Routes>
                    <Route
                      path="/"
                      element={
                        <Requiere permisos={['tablero.ver']}>
                          <PaginaTablero />
                        </Requiere>
                      }
                    />
                    <Route
                      path="/mostrador"
                      element={
                        <Requiere permisos={['mostrador.consultar', 'mostrador.pagar']}>
                          <PaginaMostrador />
                        </Requiere>
                      }
                    />
                    <Route
                      path="/integrantes"
                      element={
                        <Requiere permisos={['integrantes.ver']}>
                          <PaginaIntegrantes />
                        </Requiere>
                      }
                    />
                    <Route
                      path="/listado"
                      element={
                        <Requiere permisos={['actividades.ver', 'mostrador.consultar']}>
                          <PaginaListado />
                        </Requiere>
                      }
                    />
                    <Route
                      path="/actividades"
                      element={
                        <Requiere permisos={['actividades.ver']}>
                          <PaginaActividades />
                        </Requiere>
                      }
                    />
                    <Route
                      path="/pagos"
                      element={
                        <Requiere permisos={['pagos.ver']}>
                          <PaginaPagos />
                        </Requiere>
                      }
                    />
                    <Route
                      path="/catalogos"
                      element={
                        <Requiere permisos={['integrantes.ver', 'zonas.gestionar', 'tipos.gestionar', 'medios.gestionar', 'entidad.configurar']}>
                          <PaginaCatalogos />
                        </Requiere>
                      }
                    />
                    <Route
                      path="/equipo"
                      element={
                        <Requiere permisos={['usuarios.gestionar', 'roles.gestionar']}>
                          <PaginaEquipo />
                        </Requiere>
                      }
                    />
                    <Route
                      path="/auditoria"
                      element={
                        <Requiere permisos={['auditoria.ver']}>
                          <PaginaAuditoria />
                        </Requiere>
                      }
                    />
                    <Route path="/no-incluido/:modulo" element={<PaginaNoIncluido />} />
                    <Route path="/mi-cuenta" element={<PaginaMiCuenta />} />
                    <Route path="/entidades" element={<PaginaElegirEntidad />} />
                    <Route path="*" element={<Navigate to={inicio} replace />} />
                  </Routes>
                ) : (
                  <Routes>
                    <Route path="/" element={<PaginaPlataforma />} />
                    <Route path="/mi-cuenta" element={<PaginaMiCuenta />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                )}
              </Suspense>
            </Marco>
          }
        />
      </Routes>
    </Suspense>
  );
}
